import React, { forwardRef, useEffect, useRef, useState } from 'react';

type ImageWithPlaceholderProps = React.ComponentPropsWithoutRef<'img'> & {
  placeholderSrc?: string;
  fallbackSrc?: string;
  onLoad?: () => void;
  onError?: () => void;
};

export const ImageWithPlaceholder = forwardRef<HTMLImageElement, ImageWithPlaceholderProps>(
  ({ src, placeholderSrc, fallbackSrc, onLoad, onError, ...props }, ref) => {
    const [loadedSrc, setLoadedSrc] = useState<string | undefined>(undefined);
    const [errorSrc, setErrorSrc] = useState<string | undefined>(undefined);
    const onLoadRef = useRef(onLoad);
    const onErrorRef = useRef(onError);

    useEffect(() => {
      onLoadRef.current = onLoad;
      onErrorRef.current = onError;
    }, [onLoad, onError]);

    useEffect(() => {
      if (!src || !placeholderSrc) {
        return;
      }

      const img = new Image();

      img.onload = () => {
        setLoadedSrc(src);
        setErrorSrc(undefined);
        onLoadRef.current?.();
      };

      img.onerror = () => {
        setErrorSrc(src);
        onErrorRef.current?.();
      };

      img.src = src;

      return () => {
        img.onload = null;
        img.onerror = null;
      };
    }, [src, placeholderSrc]);

    const handleNativeLoad = () => {
      if (!placeholderSrc && src) {
        setLoadedSrc(src);
        setErrorSrc(undefined);
        onLoadRef.current?.();
      }
    };

    const handleNativeError = () => {
      if (!placeholderSrc && src) {
        setErrorSrc(src);
        onErrorRef.current?.();
      }
    };

    const hasError = errorSrc === src;
    const isLoaded = loadedSrc === src;

    const getDisplaySrc = (): string | undefined => {
      if (hasError) {
        return fallbackSrc ?? placeholderSrc ?? src;
      }

      if (placeholderSrc && !isLoaded) {
        return placeholderSrc;
      }

      return src;
    };

    return (
      <img
        ref={ref}
        src={getDisplaySrc()}
        onLoad={handleNativeLoad}
        onError={handleNativeError}
        {...props}
      />
    );
  }
);

ImageWithPlaceholder.displayName = 'ImageWithPlaceholder';
